/** Which of the three looks the archive is wearing.
 *
 *  A single attribute on <html>, read by index.css. No context, no provider,
 *  no re-render of 21,660 rows to change a colour -- the CSS custom properties
 *  do all of it, and React never hears about the switch.
 *
 *  Every localStorage touch is wrapped. Safari in some privacy modes throws on
 *  the property access itself, and this runs before the first paint: an
 *  uncaught exception here is not a missing preference, it is a blank archive.
 */
export const THEMES = ["minecraft", "normal", "genshin"] as const;
export type Theme = (typeof THEMES)[number];

/** Minecraft first, and that ordering is load-bearing twice over: THEMES[0] is
 *  what a fresh phone opens in AND where anything unrecognised lands. It is
 *  first so the timeline matches the birthday page you just came through -- the
 *  login is Minecraft unconditionally, and arriving in the dark mode a second
 *  later read like a different app. 'normal' is still the palette the rest of
 *  the file is written in (:root IS normal); it just is not the default any
 *  more, which is a token question rather than an ordering one. */
export const THEME_LABEL: Record<Theme, string> = {
  minecraft: "Minecraft",
  normal: "Normal",
  genshin: "Genshin",
};

export const KEY = "snaps.theme";

export const isTheme = (v: unknown): v is Theme =>
  typeof v === "string" && (THEMES as readonly string[]).includes(v);

export const nextTheme = (t: Theme): Theme =>
  THEMES[(THEMES.indexOf(t) + 1) % THEMES.length];

/** What was stored, if it is still one of ours. A value that fails isTheme is
 *  discarded rather than written onto the document: an unknown attribute
 *  matches no rule, so the archive would render unstyled and look broken. */
export function loadTheme(): Theme {
  try {
    const raw = localStorage.getItem(KEY);
    if (isTheme(raw)) return raw;
  } catch {
    // Storage unavailable. A preference is not worth the app for.
  }
  return THEMES[0];
}

export function saveTheme(t: Theme): void {
  try {
    localStorage.setItem(KEY, t);
  } catch {
    // The choice lasts this session only. Better than a crash on tap.
  }
}

export const applyTheme = (t: Theme): void => {
  document.documentElement.dataset.theme = t;
};
